import { TextField } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { InputComponent } from '..';

export interface InputTextAreaProps extends InputComponent {
  rows?: number;
  /**
   * When set, the typed value is mirrored locally and committed to Formik only
   * this many ms after the user stops typing. Use on large forms that re-render
   * expensively per keystroke so typing stays smooth on slower machines
   * (SUP-170). Omit for the default per-keystroke behavior.
   *
   * The value is also committed immediately on blur and on unmount, so normal
   * flows (blur, then submit) never lose edits. The one gap is a programmatic
   * submit fired while this field is still focused and within the debounce
   * window — blur the field before such a submit (a click on a Save button
   * already does this).
   */
  debounceMs?: number;
}

export function InputTextArea({
  className,
  disabled,
  hidden,
  label,
  hint,
  nameOverride,
  rows = 3,
  required,
  readOnly = false,
  showLabel = !hidden,
  placeholder = '',
  debounceMs,
}: InputTextAreaProps) {
  const { errors } = useFormikContext<Record<string, string>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const [field, , helpers] = useField<string>({ name, validate });

  // Debounced mode keeps a local mirror so keystrokes stay responsive while the
  // Formik write (which can trigger an expensive form-wide re-render) is
  // deferred until the user pauses. Unused in the default per-keystroke path.
  const [localValue, setLocalValue] = useState<string>(field.value ?? '');
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Newest value awaiting commit. Read by the unmount flush, whose cleanup
  // closure would otherwise capture a stale value.
  const latest = useRef<string>(field.value ?? '');

  // Resync from Formik on external changes (reset / reinitialize), but never
  // while the user is actively typing into this field (would drop keystrokes).
  useEffect(() => {
    if (!focused.current) {
      setLocalValue(field.value ?? '');
      latest.current = field.value ?? '';
    }
  }, [field.value]);

  // On unmount, FLUSH any still-pending commit instead of dropping it, so
  // navigating away mid-type doesn't lose the last edits. `timer.current` is
  // cleared after every commit/blur, so this is a no-op unless a debounce is
  // genuinely in flight. helpers.setValue writes to the (still-mounted) parent
  // form, so it's safe during this component's unmount.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        helpers.setValue(latest.current, true);
      }
    },
    // Run only on unmount; helpers.setValue is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const inputPlaceholder = useMemo(
    () => placeholder || label?.replace(/[?:]/g, '') || '',
    [placeholder, label],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value;
      latest.current = next;
      setLocalValue(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        helpers.setValue(next);
        timer.current = undefined; // commit done — nothing left to flush
      }, debounceMs);
    },
    [debounceMs, helpers],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      focused.current = false;
      // Flush the pending value (and validate it) so blur-time errors are correct.
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = undefined;
      }
      helpers.setValue(localValue, true);
      field.onBlur(e);
    },
    [field, helpers, localValue],
  );

  // Default path spreads Formik's field verbatim (byte-identical to before);
  // debounced path drives the input from local state with deferred commits.
  const valueProps = debounceMs
    ? {
        name,
        value: localValue,
        onChange: handleChange,
        onBlur: handleBlur,
        onFocus: () => {
          focused.current = true;
        },
      }
    : { ...field, value: field.value || '' };

  return (
    <TextField
      multiline
      fullWidth
      variant="outlined"
      rows={rows}
      label={showLabel && label}
      className={className}
      disabled={disabled}
      hidden={hidden}
      required={required && !readOnly}
      placeholder={inputPlaceholder}
      slotProps={{
        inputLabel: {
          shrink: !!(showLabel && label),
        },
        input: {
          inputProps: {
            readOnly,
          } as React.HTMLAttributes<HTMLTextAreaElement>,
        },
      }}
      error={!!error}
      helperText={error ? error : hint}
      {...valueProps}
    />
  );
}
